import {observer} from "mobx-react";
import {authStore, SlsCustomerItem, SlsUserItem} from "../../../store";
import {FieldAutoComplete, FieldInput, FieldInputWithButton, FormSection, Linq, ScrollTable} from "../../ui";
import {
    PlacementProcessDocumentDto,
    PlacementProcessDocumentStatusDto,
    PlacementProcessOriginDto,
    PlacementProcessStatusDto
} from "../../../api/sls";
import {
    convertDocumentType,
    convertPlacementProcessDocumentStatus,
    convertPlacementProcessOrigin,
    convertPlacementProcessStatus
} from "../../../api";
import {useNavigate} from "react-router";

export interface SlsFormData {
    correlationId: string
    user: SlsUserItem | undefined
    customer: SlsCustomerItem | undefined
    origin: PlacementProcessOriginDto | undefined
    status: PlacementProcessStatusDto | undefined
    documents: PlacementProcessDocumentDto[] | undefined
}

export interface SlsFormValidation {
    correlationId?: string
    user?: string
    customer?: string
}

interface SlsFormProps {
    mode: 'create' | 'update'
    validation: SlsFormValidation
    disabled?: boolean
    data: SlsFormData
    customers: SlsCustomerItem[]
    users: SlsUserItem[]
    onBlur: () => any
    onChange: (value: SlsFormData) => any
    onSearchCustomer: (query: string) => Promise<SlsCustomerItem[]>
    onSearchUser: (query: string) => Promise<SlsUserItem[]>
    onRemovePlacementProcessDocument: (documentId: string) => any
}

function generateCorrelationID(customer: string) {
    return customer + "-" + new Date().toISOString().replace(/\D/g, '');
}

function crateDocumentStatusDisplay(documentStatusDto: PlacementProcessDocumentStatusDto, processStatus: PlacementProcessStatusDto) {
    if (processStatus === PlacementProcessStatusDto.CLOSED) {
        return documentStatusDto === PlacementProcessDocumentStatusDto.CLARIFICATION
            ? convertPlacementProcessDocumentStatus(PlacementProcessDocumentStatusDto.CLARIFICATION)
            : convertPlacementProcessStatus(processStatus)
    }
    return convertPlacementProcessDocumentStatus(documentStatusDto);
}

interface PlacementProcessLineProps {
    status: PlacementProcessStatusDto | undefined
    customer: SlsCustomerItem | undefined
    val: PlacementProcessDocumentDto
    onRemovePlacementProcessDocument: (documentId: string) => any
}

function PlacementProcessLine({
                                  status,
                                  customer,
                                  val,
                                  onRemovePlacementProcessDocument
                              }: PlacementProcessLineProps) {
    const clsName = val.status === PlacementProcessDocumentStatusDto.READY ? 'has-text-success'
        : (
            val.status === PlacementProcessDocumentStatusDto.CLARIFICATION ? 'has-text-danger has-text-weight-bold' : ''
        )
    const arDisabled = status !== PlacementProcessStatusDto.ACTIVE || val.status !== PlacementProcessDocumentStatusDto.ANNOUNCED
    const hasVda = Boolean(val.vdaId)
    const navigate = useNavigate()

    return <>
        <tr>
            <td style={{display: 'flex'}}>
                <>
                    <span style={{paddingRight: '1em'}}>{val.vin}</span>
                    {customer?.id && <Linq
                        to={`/vhs/${customer?.id}/${val.vin}`}><span
                        className="material-symbols-outlined has-text-link">link</span></Linq>}
                </>
            </td>
            <td>{val.documentId}</td>
            <td>{convertDocumentType(val.documentType)}</td>
            <td className={clsName}>{crateDocumentStatusDisplay(val.status, status!)}</td>
            {status === PlacementProcessStatusDto.CLOSED && <td>{val.storageLocation || ''}</td>}
            <td
                className={'has-text-primary'}
                style={{
                    textAlign: 'right'
                }}
            >

                <button
                    title="Fahrzeugerfassung"
                    disabled={!hasVda}
                    onClick={() => {
                        navigate(`/vda/${val.vdaId}`)
                    }}
                >
                    <span
                        className={`material-symbols-outlined ${!hasVda && 'has-text-grey-light'}`}
                        style={{
                            cursor: 'pointer'
                        }}

                    >expand_circle_right</span>
                </button>

                {authStore.hasRole('DOCUMENT_ANNOUNCEMENT_REVOKE') && <button
                    disabled={arDisabled}
                    title="Dokumentenankündigung zurücknehmen"
                    onClick={() => {
                        onRemovePlacementProcessDocument(val.documentId)
                    }}
                >
                    <span
                        className={`material-symbols-outlined ${arDisabled && 'has-text-grey-light'}`}
                        style={{
                            cursor: 'pointer'
                        }}

                    >delete</span>
                </button>}
            </td>
        </tr>
    </>
}

const SlsForm = observer(function SlsForm({
                                              data,
                                              validation,
                                              disabled,
                                              customers,
                                              users,
                                              mode,
                                              onBlur,
                                              onChange,
                                              onSearchCustomer,
                                              onSearchUser,
                                              onRemovePlacementProcessDocument
                                          }: SlsFormProps) {
    const {
        correlationId,
        user,
        customer,
        documents,
        status,
        origin
    } = data

    return <>
        <div style={{overflow: 'visible', padding: '1em'}}>
            <FormSection
                label={"Allgemein"}
                children={
                    <>
                        <div className={"columns"}>
                            <div className={"column is-one-third"}>
                                <FieldInputWithButton
                                    label={"Kennung"}
                                    id={'CorrelationId'}
                                    disabled={mode === 'update'}
                                    readonly={true}
                                    required={true}
                                    invalid={Boolean(validation.correlationId)}
                                    help={validation.correlationId}
                                    value={correlationId}
                                    onBlur={async () => !disabled && onBlur()}
                                    onChange={correlationId => {
                                        onChange({
                                            ...data,
                                            correlationId: correlationId
                                        })
                                        onBlur()
                                    }}
                                    buttonRight={
                                        <button
                                            title="Neu generieren"
                                            onClick={() => {
                                                onChange({
                                                    ...data,
                                                    correlationId: generateCorrelationID(customer?.customerNumber!!)
                                                })
                                                onBlur()
                                            }}
                                            disabled={mode === 'update' || !Boolean(data.customer)}
                                            className={'button is-primary'}>

                                            <span className="material-symbols-outlined">autorenew</span>
                                        </button>
                                    }
                                />
                            </div>
                            <div className={"column is-one-third"}>
                                <FieldInput
                                    label={"Quelle"}
                                    disabled={true}
                                    required={true}
                                    value={convertPlacementProcessOrigin(origin)}
                                />
                            </div>
                            <div className={"column is-one-third"}>
                                <FieldInput
                                    label={"Status"}
                                    disabled={true}
                                    required={true}
                                    value={convertPlacementProcessStatus(status)}
                                />
                            </div>
                        </div>
                        <div className={"columns"}>
                            <div className={"column is-one-third"}>
                                <FieldAutoComplete
                                    label={"Kunde"}
                                    disabled={disabled}
                                    required={true}
                                    help={validation.customer}
                                    invalid={Boolean(validation.customer)}
                                    value={customer}
                                    options={customers}
                                    itemRenderer={c => c.label}
                                    search={onSearchCustomer}
                                    onBlur={() => !disabled && onBlur()}
                                    onChange={(customer: SlsCustomerItem | undefined) => {
                                        onChange({
                                            ...data,
                                            customer: customer!!,
                                            correlationId: customer ? generateCorrelationID(customer.customerNumber) : ''
                                        })
                                        onBlur()
                                    }}
                                />
                            </div>
                            <div className={"column is-one-third"}>
                                <FieldAutoComplete
                                    label={"User"}
                                    disabled={disabled}
                                    required={true}
                                    help={validation.user}
                                    invalid={Boolean(validation.user)}
                                    value={user}
                                    options={users}
                                    itemRenderer={c => c.label}
                                    search={onSearchUser}
                                    onBlur={() => !disabled && onBlur()}
                                    onChange={(user: SlsUserItem | undefined) => {
                                        onChange({
                                            ...data,
                                            user: user!!
                                        })
                                        onBlur()
                                    }}
                                />
                            </div>
                        </div>
                    </>
                }
            />
        </div>
        {mode === 'update' &&
            <div style={{flex: '1', display: 'flex'}}>
                <ScrollTable>
                    <thead>
                    <tr>
                        <th style={{width: '18.5em'}}>FIN</th>
                        <th>Dokumenten-Id</th>
                        <th>Dokumentenart</th>
                        <th>Status</th>
                        {status === PlacementProcessStatusDto.CLOSED && <th>Lagerort</th>}
                        <th style={{width: '10em', textAlign: 'right'}}>Erfassung</th>
                    </tr>
                    </thead>
                    <tbody>
                    {documents?.map((val, index) => <PlacementProcessLine
                        key={index}
                        status={status}
                        customer={customer}
                        onRemovePlacementProcessDocument={onRemovePlacementProcessDocument}
                        val={val}
                    />)}
                    </tbody>
                </ScrollTable>
            </div>
        }
    </>
})

export {
    SlsForm
}
